import { useCallback, useMemo, useRef, useState } from 'react';
import { readPersistedTableFilters, type TableQuery } from './WbsTable';

const DEFAULT_PAGE_SIZE = 20;

/**
 * The query params a v3 list endpoint expects: the shared pagination pair plus
 * the list controls. Extra keys are the mapped filter params.
 */
export interface WbsTableQueryParams {
  offset: number;
  limit: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  [param: string]: unknown;
}

export interface UseWbsTableQueryOptions {
  /** Rows per page; becomes both `limit` and the table's `itemsPerPage`. */
  pageSize?: number;
  /** Passed through to `WbsTable` to key its "remember my filters" storage. */
  tableKey?: string;
  /**
   * Translate the filter-panel values into endpoint query params (the panel's
   * keys are UI keys and rarely match the API's). Values that come back empty
   * are dropped. Without it the filter keys are sent verbatim.
   */
  mapFilters?: (filters: Record<string, unknown>) => Record<string, unknown>;
  /**
   * Query params applied UNTIL the filter panel is first applied or reset — a
   * deep link's one-shot seed. Per key, and only where the panel has produced
   * nothing for that key, so a remembered filter still wins.
   *
   * This is what legacy's hash read is: `wbs-datatable.js:583-594` writes the
   * pairs into `controls.filters` and clears the hash without restoring them
   * into the panel, so the seed survives paging, sorting and searching (none of
   * which rebuild `controls.filters`) and dies at the first Apply/Reset, which
   * replaces that object wholesale. A permanent `value ?? seed` is NOT the same
   * thing: `params` drops an emptied filter, so clearing the control would
   * re-arm the seed and the control could never express "no filter".
   */
  seedParams?: Record<string, unknown>;
}

/** Spread onto `<WbsTable />` — this is what puts it in server-driven mode. */
export interface WbsTableQueryProps {
  itemsPerPage: number;
  tableKey: string | undefined;
  onQueryChange: (query: TableQuery) => void;
}

export interface UseWbsTableQueryResult {
  /** Last query the table emitted. */
  query: TableQuery;
  /**
   * False once the filter panel has been applied or reset — i.e. once
   * `seedParams` has been consumed and stops being merged into `params`.
   */
  seedActive: boolean;
  /** `query` projected into v3 list query params. */
  params: WbsTableQueryParams;
  tableProps: WbsTableQueryProps;
}

function isEmptyFilterValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  // The filter panel's checkboxes mean "only show X", so `false` is "no filter".
  if (value === false) return true;
  return Array.isArray(value) && value.length === 0;
}

function isSameQuery(a: TableQuery, b: TableQuery): boolean {
  if (
    a.search !== b.search ||
    a.sort !== b.sort ||
    a.order !== b.order ||
    a.page !== b.page
  ) {
    return false;
  }
  const aKeys = Object.keys(a.filters);
  const bKeys = Object.keys(b.filters);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => Object.is(a.filters[key], b.filters[key]));
}

/**
 * Owns the search / filter / sort / page state of a server-driven `WbsTable`
 * and projects it into v3 list query params.
 *
 * ```tsx
 * const table = useWbsTableQuery({ tableKey: 'student-board' });
 * const { data, isFetching, refetch } = api.getStudentBoard.useQuery({ query: table.params });
 * <WbsTable {...table.tableProps} data={rows} columns={columns}
 *           totalCount={data?.count} loading={isFetching} onRefresh={refetch} />
 * ```
 *
 * `WbsTable` is the single source of truth for the query — the hook mirrors
 * what the table emits, so there is deliberately no setter here.
 */
export function useWbsTableQuery(
  options: UseWbsTableQueryOptions = {},
): UseWbsTableQueryResult {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    tableKey,
    mapFilters,
    seedParams,
  } = options;

  const [query, setQuery] = useState<TableQuery>(() => ({
    search: '',
    // Match what the table will restore, so the first request is already the
    // remembered one rather than an unfiltered request we immediately redo.
    filters: readPersistedTableFilters(tableKey),
    sort: null,
    order: 'asc',
    page: 1,
  }));

  // `WbsTable` holds its applied filters in one state object and replaces it
  // wholesale on Apply (`WbsTable.tsx:389`) and Reset (`:397`) and at no other
  // time — paging, sorting and searching all re-emit the SAME object. So the
  // first `filters` identity we are handed is the mount emit's, and the first
  // one that differs from it is the user's first Apply/Reset. That identity, not
  // a value comparison, is what retires the seed: applying a panel whose Study
  // Helper control is empty is a real "no helper filter" and compares equal to
  // the mount emit.
  const mountFiltersRef = useRef<Record<string, unknown> | null>(null);
  const [seedActive, setSeedActive] = useState(true);

  // Returning the previous object when nothing moved keeps the table's mount
  // emit (and React StrictMode's double-invoke) from causing a re-render.
  const onQueryChange = useCallback((next: TableQuery) => {
    if (mountFiltersRef.current === null) {
      mountFiltersRef.current = next.filters;
    } else if (next.filters !== mountFiltersRef.current) {
      setSeedActive(false);
    }
    setQuery((prev) => (isSameQuery(prev, next) ? prev : next));
  }, []);

  const params = useMemo<WbsTableQueryParams>(() => {
    const next: WbsTableQueryParams = {
      offset: (query.page - 1) * pageSize,
      limit: pageSize,
    };
    const filters = mapFilters ? mapFilters(query.filters) : query.filters;
    for (const [key, value] of Object.entries(filters)) {
      if (isEmptyFilterValue(value)) continue;
      next[key] = value;
    }
    if (seedActive && seedParams) {
      for (const [key, value] of Object.entries(seedParams)) {
        if (isEmptyFilterValue(value)) continue;
        if (next[key] === undefined) next[key] = value;
      }
    }
    const search = query.search.trim();
    if (search !== '') next.search = search;
    if (query.sort !== null) {
      next.sort = query.sort;
      next.order = query.order;
    }
    return next;
  }, [query, pageSize, mapFilters, seedActive, seedParams]);

  const tableProps = useMemo<WbsTableQueryProps>(
    () => ({ itemsPerPage: pageSize, tableKey, onQueryChange }),
    [pageSize, tableKey, onQueryChange],
  );

  return { query, seedActive, params, tableProps };
}
